/**
 * Scenario Type Vocabulary
 * ─────────────────────────────────────────────────────────────────────────────
 * One row per `RolePlayType` enum member, holding every per-type fact the app
 * used to restate: what to call it, which URL slug it lives under, where its
 * admin editor is (`editRoute`, under /workspaces/admin, read through
 * `lib/admin-scenario-links.js`), which Prisma delegate holds the scenario itself, which Prisma column / relation / model
 * carries its simulation result, and how a result row points back at its
 * scenario, persona and user (the `result*` columns, read through
 * `lib/results/result-kinds.js`).
 *
 * Before this the six types were spelled out in ~25 object literals and the
 * Outbound & Inbound module alone shipped under five names ("Outbound &
 * Inbound", "Outbound", "Cold Call", "Cold call", "Cold calling"). Adding a
 * seventh type meant finding all of them.
 *
 * Two label columns, because the app genuinely needs two words:
 *   - `moduleLabel`   the module / category ("Objections", "Plays") — what a
 *                     filter tab, report column or picker heading says. A
 *                     studio can rename this; resolve through
 *                     `getModuleLabelForScenarioType` when enabled modules are
 *                     to hand.
 *   - `scenarioLabel` one scenario of that type ("Objection", "Play") — what a
 *                     badge on a single assignment says.
 * Anything that needs a third spelling is drift; add a column, not a local map.
 *
 * Keep this file free of React, Prisma, lucide and Tailwind: emails, server
 * actions and client components all import it. Colour and icon per type live in
 * `lib/role-play-theme.js` (app) and `lib/email/practice-types.js` (email),
 * both of which read their labels from here.
 */

export const SCENARIO_TYPES = {
  OBJECTIONS: {
    moduleLabel: "Objections",
    scenarioLabel: "Objection",
    fieldLabel: "customer objection",
    route: "objections",
    editRoute: "data/objections/edit/:id",
    phoneSimType: "objections",
    scenarioModel: "customerObjection",
    scenarioIdField: "customerObjectionId",
    titleField: "objection",
    resultIdField: "objectionSimulationResultId",
    resultRelation: "objectionSimulationResult",
    resultModel: "objectionSimulationResult",
    resultScenarioIdField: "customerObjectionId",
    resultScenarioRelation: "CustomerObjection",
    resultPersonaRelation: "CustomerPersona",
    resultUserRelation: "User",
    resultIdType: "int",
    resultWhere: {},
  },
  PITCH: {
    moduleLabel: "Messaging / Pitch",
    scenarioLabel: "Pitch",
    fieldLabel: "pitch",
    route: "pitch",
    editRoute: "data/pitch/edit/:id",
    phoneSimType: "pitch",
    scenarioModel: "pitch",
    scenarioIdField: "pitchId",
    titleField: "pitch",
    resultIdField: "pitchSimulationResultId",
    resultRelation: "pitchSimulationResult",
    resultModel: "pitchSimulationResult",
    resultScenarioIdField: "pitchId",
    resultScenarioRelation: "Pitch",
    resultPersonaRelation: "CustomerPersona",
    resultUserRelation: "User",
    resultIdType: "int",
    resultWhere: {},
  },
  DISCOVERY: {
    moduleLabel: "Discovery",
    scenarioLabel: "Discovery",
    fieldLabel: "discovery scenario",
    route: "discovery",
    editRoute: "data/discovery/edit/:id",
    phoneSimType: "discovery",
    scenarioModel: "discovery",
    scenarioIdField: "discoveryId",
    titleField: "trackName",
    resultIdField: "discoverySimulationResultId",
    resultRelation: "discoverySimulationResult",
    resultModel: "discoverySimulationResult",
    resultScenarioIdField: "discoveryId",
    resultScenarioRelation: "Discovery",
    resultPersonaRelation: "CustomerPersona",
    resultUserRelation: "User",
    resultIdType: "int",
    resultWhere: {},
  },
  PLAY: {
    moduleLabel: "Plays",
    scenarioLabel: "Play",
    fieldLabel: "play",
    route: "plays",
    editRoute: "data/plays/edit/:id",
    // The phone result tables key plays as "play" where every route says
    // "plays"; this is the one type whose two slugs differ.
    phoneSimType: "play",
    scenarioModel: "play",
    scenarioIdField: "playId",
    titleField: "name",
    resultIdField: "playSimulationResultId",
    resultRelation: "playSimulationResult",
    resultModel: "playSimulationResult",
    resultScenarioIdField: "playId",
    resultScenarioRelation: "Play",
    resultPersonaRelation: "CustomerPersona",
    resultUserRelation: "User",
    resultIdType: "int",
    resultWhere: {},
  },
  COLD_CALL: {
    moduleLabel: "Outbound & Inbound",
    // The only type whose two labels are the same word: the module has no
    // natural singular, so a badge on one assignment says "Outbound & Inbound"
    // the way another says "Discovery". Inventing one is how the five
    // spellings started.
    scenarioLabel: "Outbound & Inbound",
    fieldLabel: "outbound & inbound scenario",
    route: "cold-call",
    editRoute: "data/cold-call/edit/:id",
    phoneSimType: "cold-call",
    scenarioModel: "coldCall",
    scenarioIdField: "coldCallId",
    titleField: "name",
    resultIdField: "coldCallSimulationResultId",
    resultRelation: "coldCallSimulationResult",
    // Outbound & Inbound is the one type with no table of its own — its
    // results live in the generic SimulationResult.
    resultModel: "simulationResult",
    resultScenarioIdField: "simulationId",
    // SimulationResult keeps the scenario id in a plain column with no
    // relation, so a cold-call result reads its ColdCall by id instead.
    resultScenarioRelation: null,
    resultPersonaRelation: "CustomerPersona",
    resultUserRelation: "User",
    resultIdType: "int",
    // The generic table also holds on-the-fly results; this tells them apart.
    resultWhere: { simulationType: "COLD_CALL" },
  },
  MANAGEMENT: {
    moduleLabel: "Management Practice",
    scenarioLabel: "Management",
    fieldLabel: "management scenario",
    route: "management",
    // The one admin editor whose folder and id order differ from its route.
    editRoute: "data/management-scenarios/:id/edit",
    phoneSimType: "management",
    scenarioModel: "managementScenario",
    scenarioIdField: "managementScenarioId",
    titleField: "name",
    resultIdField: "managementSimulationResultId",
    resultRelation: "managementSimulationResult",
    resultModel: "managementSimulationResult",
    resultScenarioIdField: "managementScenarioId",
    resultScenarioRelation: "managementScenario",
    resultPersonaRelation: "sellerPersona",
    resultUserRelation: "user",
    // The one result table keyed by cuid; the other five use an integer.
    resultIdType: "cuid",
    resultWhere: {},
  },
};

// Rows sit in the Prisma `RolePlayType` enum's order. Nothing displays that
// order; the derived maps only iterate it, and scenarioTypes.test.js pins it
// to catch a missing or extra row.
export const SCENARIO_TYPE_KEYS = Object.keys(SCENARIO_TYPES);

function columnMap(column) {
  return Object.fromEntries(
    SCENARIO_TYPE_KEYS.map((type) => [type, SCENARIO_TYPES[type][column]])
  );
}

/** RolePlayType → the module / category name. */
export const ROLE_PLAY_TYPE_MODULE_LABELS = columnMap("moduleLabel");
/** RolePlayType → the name of one scenario of that type. */
export const ROLE_PLAY_TYPE_SCENARIO_LABELS = columnMap("scenarioLabel");
/** RolePlayType → the URL slug under /simulator, /dashboard and /results. */
export const ROLE_PLAY_TYPE_ROUTES = columnMap("route");
/** RolePlayType → the phone simulator's simulationType key. */
export const ROLE_PLAY_TYPE_PHONE_SIM_TYPES = columnMap("phoneSimType");
/** RolePlayType → the RolePlayAssignment column holding its result id. */
export const ROLE_PLAY_TYPE_RESULT_ID_FIELDS = columnMap("resultIdField");
/** RolePlayType → the RolePlayAssignment relation to its result row. */
export const ROLE_PLAY_TYPE_RESULT_RELATIONS = columnMap("resultRelation");
/** RolePlayType → the Prisma delegate holding its results. */
export const ROLE_PLAY_TYPE_RESULT_MODELS = columnMap("resultModel");
/** RolePlayType → the Prisma delegate holding the scenario itself. */
export const ROLE_PLAY_TYPE_SCENARIO_MODELS = columnMap("scenarioModel");

/**
 * Phone simulator simulationType key → the module / category name. Keyed by
 * `phoneSimType`, which `scenarioTypes.test.js` › "keeps the key columns
 * unique" holds — two rows sharing one key would silently drop a label here,
 * last row wins.
 */
export const PHONE_SIM_TYPE_MODULE_LABELS = Object.fromEntries(
  SCENARIO_TYPE_KEYS.map((type) => [
    SCENARIO_TYPES[type].phoneSimType,
    SCENARIO_TYPES[type].moduleLabel,
  ])
);

/** URL slug → RolePlayType, for reading a type back off a route. */
export const ROUTE_TO_ROLE_PLAY_TYPE = Object.fromEntries(
  SCENARIO_TYPE_KEYS.map((type) => [SCENARIO_TYPES[type].route, type])
);

// Every spelling a type arrives in — the enum, the route slug, the phone key,
// the module name, and the singular/plural/legacy aliases that older links and
// payloads still send. Keys are compared with separators and case flattened, so
// "COLD_CALL", "cold-call" and "Cold Call" are one entry.
function normalizeKey(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
}

const TYPE_BY_ALIAS = new Map();
function alias(value, type) {
  if (value) TYPE_BY_ALIAS.set(normalizeKey(value), type);
}
for (const type of SCENARIO_TYPE_KEYS) {
  const row = SCENARIO_TYPES[type];
  for (const spelling of [
    type,
    row.route,
    row.phoneSimType,
    row.moduleLabel,
    row.scenarioLabel,
  ]) {
    alias(spelling, type);
  }
}
// Legacy and shorthand names that no column produces.
alias("objection", "OBJECTIONS");
alias("customer objection", "OBJECTIONS");
alias("plays", "PLAY");
alias("messaging", "PITCH");
alias("pitches", "PITCH");
alias("coldcall", "COLD_CALL");
alias("outbound", "COLD_CALL");
alias("management scenario", "MANAGEMENT");

/**
 * The RolePlayType behind any spelling of a scenario type — the enum value, the
 * route slug, the phone key, a module name, or a legacy alias — or null when it
 * names none.
 */
export function toRolePlayType(value) {
  if (!value) return null;
  return TYPE_BY_ALIAS.get(normalizeKey(value)) ?? null;
}

/** One row of the table for any spelling of a type, or null. */
export function getScenarioType(value) {
  const type = toRolePlayType(value);
  return type ? SCENARIO_TYPES[type] : null;
}

/** The module / category name for any spelling, falling back to `fallback`. */
export function scenarioModuleLabel(value, fallback = "") {
  return getScenarioType(value)?.moduleLabel ?? fallback;
}

/** The single-scenario name for any spelling, falling back to `fallback`. */
export function scenarioLabel(value, fallback = "") {
  return getScenarioType(value)?.scenarioLabel ?? fallback;
}

/** The URL slug for any spelling, falling back to `fallback`. */
export function scenarioRoute(value, fallback = null) {
  return getScenarioType(value)?.route ?? fallback;
}

// On-the-Fly practice is not a RolePlayType — it has no scenario table and no
// assignment — but its results live under /dashboard/on-the-fly/results/:id
// like every other kind, so the result-route helper has to know its slug.
export const ON_THE_FLY_ROUTE = "on-the-fly";

/** Whether a type or slug names On-the-Fly practice, in any spelling. */
export function isOnTheFly(typeOrSlug) {
  return Boolean(typeOrSlug) && normalizeKey(typeOrSlug) === ON_THE_FLY_ROUTE;
}

/**
 * The `/dashboard/<segment>/results/:id` slug for a result of any kind.
 *
 * @param {string} typeOrSlug Any spelling the app stores a type in: the
 *   RolePlayType enum, a route slug, a phone simulator key, or `ON_THE_FLY`.
 * @param {string|null} [fallback] Returned when nothing resolves.
 */
export function resultRouteSegment(typeOrSlug, fallback = null) {
  if (!typeOrSlug) return fallback;
  if (isOnTheFly(typeOrSlug)) return ON_THE_FLY_ROUTE;
  return scenarioRoute(typeOrSlug, fallback);
}
